import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  extractFromPdf,
  selectExtracting,
  selectExtractMeta
} from '../../redux/slices/applicationSlice';
import { notify } from '../../utils/notify';
import PropTypes from 'prop-types';
import './index.scss';

const MAX_BYTES = 10 * 1024 * 1024;

// Live PDF import. Reads a digital PDF and fills the wizard for the
// applicant to REVIEW. Nothing is auto-submitted, and the imported PDF
// is not stored — it's read for its text and discarded server-side.
const ImportPanel = ({ onExtracted }) => {
  const dispatch = useDispatch();
  const extracting = useSelector(selectExtracting);
  const meta = useSelector(selectExtractMeta);

  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [documentsReady, setDocumentsReady] = useState(null);

  const handleFile = async (file) => {
    setError(null);

    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('File is over the 10MB limit');
      return;
    }

    const result = await dispatch(extractFromPdf(file));

    if (extractFromPdf.fulfilled.match(result)) {
      const { fields, meta: resultMeta } = result.payload;
      if (resultMeta.foundCount === 0) {
        notify.info("We couldn't find any details to import. Please enter them manually.");
        return;
      }
      notify.success(`Imported ${resultMeta.foundCount} field(s) — please review each one`);
      onExtracted?.(fields);
    } else {
      // The rejected payload holds our friendly server message.
      const message = result.payload || 'Could not read this document';
      setError(message);
      notify.error(message);
    }
  };

  return (
    <div className={`import-panel ${dragging ? 'import-panel--dragging' : ''}`}>
      {/* <div className="import-panel__head">
        <span className="import-panel__title">Upload your ID to auto-fill</span>
      </div> */}
      <div className="import-panel__head">
        <span className="import-panel__title">Do you have your documents?</span>
      </div>

      <div className="import-panel__toggle">
        <button
          type="button"
          className={`import-panel__toggle-btn ${
            documentsReady === true ? 'import-panel__toggle-btn--active' : ''
          }`}
          onClick={() => setDocumentsReady(true)}>
          Yes
        </button>

        <button
          type="button"
          className={`import-panel__toggle-btn ${
            documentsReady === false ? 'import-panel__toggle-btn--active' : ''
          }`}
          onClick={() => setDocumentsReady(false)}>
          No
        </button>
      </div>

      {documentsReady === true && (
        <>
          <p className="import-panel__desc">
            {`Have a document with your personal details — like your PAN card, passport, or
  voter ID? Upload it and we'll fill in what we can. You'll review every field
  before continuing, and nothing is submitted automatically.`}
          </p>

          <button
            type="button"
            className="import-panel__zone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            disabled={extracting}>
            {extracting ? (
              <>
                <span className="import-panel__spinner" aria-hidden="true" />
                <span className="import-panel__zone-text">Reading your document…</span>
                <span className="import-panel__zone-sub">This can take a few seconds</span>
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 15V3m0 0L8 7m4-4l4 4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="import-panel__zone-text">
                  Drop a file here, or click to browse
                </span>
                <span className="import-panel__zone-sub">PDF, JPG or PNG · up to 10MB</span>
              </>
            )}
          </button>

          {error && (
            <p className="import-panel__error" role="alert">
              {error}
            </p>
          )}

          {meta && meta.foundCount > 0 && !extracting && (
            <p className="import-panel__note">
              Imported {meta.foundCount} field(s). Highlighted fields below were filled from your
              document — please check each one is correct.
            </p>
          )}

          <input
            ref={inputRef}
            type="file"
            accept=".pdf"
            className="sr-only"
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </>
      )}
    </div>
  );
};
ImportPanel.propTypes = {
  onExtracted: PropTypes.func
};

export default ImportPanel;
